import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import FAQBlock from "../components/site/FAQBlock";
import CostFactors from "../components/site/CostFactors";
import ProcessSteps from "../components/site/ProcessSteps";
import useSEO from "../hooks/useSEO";
import {
  Camera,
  Cctv,
  Eye,
  Radio,
  ShieldAlert,
  Fence,
  Bell,
  Smartphone,
  Cloud,
  ScanEye,
} from "lucide-react";

const IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/1go309ee_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_23_51%20PM.png";

export default function CCTVSecurity() {
  useSEO({
    title: "CCTV & Security Systems Ireland | Installation, Monitoring & Upgrades | FSS",
    description:
      "Commercial CCTV, IP & HD camera systems, video analytics, intruder alarms and perimeter security — installed and maintained by FSS across Ireland.",
    canonicalPath: "/cctv-security",
  });

  return (
    <>
      <PageHero
        eyebrow="CCTV & Security Systems"
        title="See what matters. Protect what matters."
        subtitle="Professional CCTV, intruder alarm and perimeter security solutions designed around the layout, risks and operational requirements of each premises. From site surveys and system design through to installation, remote viewing, monitoring integration, maintenance and upgrades — FSS provides complete security system support."
        image={IMG}
        imageAlt="Security officer inspecting an outdoor CCTV camera at night"
        ctaPrimary={{ to: "/contact", label: "Request a Security Assessment" }}
        ctaSecondary={{ to: "/contact", label: "Arrange a CCTV Survey" }}
      />

      <ServiceBody
        includedSubtitle="CCTV & Security services"
        included={[
          { icon: Camera, title: "CCTV Installation", copy: "Site survey, system design, camera positioning, installation, commissioning, remote and mobile access setup and user handover." },
          { icon: Cctv, title: "CCTV Surveillance", copy: "HD, day/night, indoor and outdoor cameras with motion and intrusion detection, remote viewing and recorded footage." },
          { icon: Cloud, title: "IP & HD Camera Systems", copy: "Network-integrated IP and HD camera systems with mobile viewing, smart alerts, IR night vision and scalable design." },
          { icon: ScanEye, title: "Video Analytics", copy: "Smart motion detection, intrusion alerts, line-crossing / area entry (where offered) and post-event search." },
          { icon: Eye, title: "Remote Viewing", copy: "Live and playback access from mobile and desktop for operational visibility across single or multiple sites." },
          { icon: ShieldAlert, title: "Intruder Alarms", copy: "Wired, wireless and hybrid intruder alarms with mobile app control, instant alerts and monitoring integration." },
          { icon: Fence, title: "Perimeter Security", copy: "External sensors, beams, barriers, gates and CCTV integration for boundary and yard protection." },
          { icon: Radio, title: "System Integration", copy: "Integrated CCTV, alarm and access control — designed to work together as one security ecosystem." },
        ]}
        whoFor={[
          "Warehouses, industrial sites and yards",
          "Retail premises and shopping centres",
          "Offices, corporate campuses and mixed-use buildings",
          "Schools, healthcare and public-sector facilities",
          "Farms, agricultural sites and construction projects",
          "Multi-site organisations requiring central coverage",
        ]}
        benefits={[
          "See and record events across your premises",
          "Deter unauthorised access and reduce operational risk",
          "Verify incidents remotely from any device",
          "Integrate with intruder alarms, access control and monitoring",
          "Scale coverage as your premises or portfolio grows",
        ]}
        defaultService="CCTV Installation"
      />

      <section id="ip-hd" className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">IP vs HD</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Choosing between IP and HD camera systems
            </h2>
            <p className="mt-3 text-slate-600">
              A short, plain-English comparison. The right choice depends on
              your existing infrastructure, image-quality needs and how you plan
              to view and store footage — which is why a site survey is always
              our starting point.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="fss-card">
              <Cloud className="w-8 h-8 text-green-700" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">IP camera systems</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">Network-connected cameras that operate over your data infrastructure. Often chosen where higher resolution, smart analytics, easy remote access and scalable expansion are important.</p>
            </div>
            <div className="fss-card">
              <Camera className="w-8 h-8 text-green-700" strokeWidth={1.75} />
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">HD camera systems</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">High-definition analogue-based systems, often chosen where existing coaxial cabling can be re-used and specific analytic features are not required.</p>
            </div>
          </div>
          <div className="mt-6 p-4 border border-slate-200 bg-slate-50 rounded-sm text-xs text-slate-600">
            We never recommend a specific system without first understanding your building, cabling, network and how footage needs to be used.
          </div>
        </div>
      </section>

      <ProcessSteps
        eyebrow="Installation process"
        title="How a CCTV or security installation works"
        steps={[
          { title: "Initial Discussion", copy: "We discuss the site, current systems and what you need to achieve." },
          { title: "Site Survey", copy: "On-site risk and coverage assessment, plus review of any existing infrastructure." },
          { title: "System Design", copy: "A recommended solution matched to the building, users and risk profile." },
          { title: "Quotation", copy: "A clear itemised proposal with expected scope and timescales." },
          { title: "Installation & Configuration", copy: "Equipment installed, configured and tested end-to-end." },
          { title: "Handover & Support", copy: "User walkthrough, documentation and ongoing maintenance options." },
        ]}
        bg="slate"
      />

      <CostFactors
        title="What affects CCTV & security cost"
        intro="Every premises is different. Following a site survey we provide a clear, itemised quotation."
        factors={[
          "Number of cameras or sensors",
          "Camera type and image quality",
          "Site size and layout",
          "Internal and external coverage needs",
          "Cable routes and network infrastructure",
          "Recording and storage duration",
          "Remote viewing requirements",
          "Whether monitoring is included",
          "Any existing equipment being re-used",
          "Installation complexity and access",
        ]}
      />

      <FAQBlock
        eyebrow="CCTV & Security FAQ"
        title="Buyer questions"
        items={[
          { q: "How much does a CCTV system cost?", a: "Cost depends on number and type of cameras, coverage area, recording and remote-viewing requirements, and site complexity. Following a survey we provide a clear quotation." },
          { q: "How many cameras does a premises need?", a: "The right number depends on coverage requirements, internal vs. external areas and the risk profile of the site. We confirm this during a site survey." },
          { q: "Can CCTV be viewed on a mobile phone?", a: "Yes — modern systems support secure mobile and desktop access for live and recorded footage." },
          { q: "What is the difference between IP and HD CCTV?", a: "IP systems use your network for higher-resolution imagery and analytics; HD systems typically use existing coaxial infrastructure. The right choice depends on the building and requirements." },
          { q: "How long can footage be stored?", a: "Storage duration depends on the number of cameras, recording quality and the storage capacity installed. We size this to suit your operational needs." },
          { q: "Does CCTV work at night?", a: "Cameras with infrared night vision or low-light capability can operate in dark conditions. We recommend the appropriate cameras for the environment." },
          { q: "What happens if the internet goes down?", a: "Local recording typically continues; remote viewing is temporarily unavailable until connectivity is restored." },
          { q: "Can FSS upgrade an existing CCTV system?", a: "In many cases yes — including additional cameras, storage upgrades or a wider system refresh, subject to the existing equipment." },
          { q: "Can CCTV integrate with intruder alarms?", a: "Yes — CCTV and intruder alarms can be integrated, including alarm-triggered recording and monitored verification." },
          { q: "Can CCTV be professionally monitored?", a: "Yes — see our Monitoring division for alarm monitoring, live CCTV monitoring and remote CCTV verification." },
          { q: "Are wireless intruder alarms reliable?", a: "Modern wireless systems are widely used in commercial and residential premises. Wired, wireless and hybrid options each suit different sites." },
          { q: "Can FSS maintain systems installed by another provider?", a: "In most cases yes — subject to the existing equipment, we can assess it, service it and support future upgrades." },
          { q: "What information is needed for a quotation?", a: "Typically the site address, a description of the premises, the areas you want to cover and any existing equipment. Photos help — a site survey confirms the detail." },
        ]}
      />

      <CTABanner
        title="See what matters. Protect what matters."
        subtitle="Contact FSS to arrange a CCTV or security site assessment."
        primary={{ to: "/contact", label: "Request a Security Assessment" }}
        secondary={{ to: "/contact", label: "Arrange a CCTV Survey" }}
      />
    </>
  );
}
